import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import MUIMultiSelect from "../../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import { InfoOutlined, AddCircleOutline } from "@mui/icons-material";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { TextField, Chip } from "@mui/material";
import { useDispatch } from "react-redux";
import classes from "./Setting.module.css";
import { useContext } from "react";
import { DiscoveryContext } from "../discovery-context";
import { AuthContext } from "../../../context/auth-context";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const MAX_RANK_COMPETITIVE_SET = 10;

export default function TemplateSettings({
    templateOptions,
    metricSettingId,
    MetricSettingChangeHandle,
    category,
    categoryWiseMetric,
    onCategoryTemplateChange,
    defaultTemplate,
    overrideCount,
    showAddButton,
    isDisabled: isDisabledProp = undefined,
    metricSettingObject: metricSettingObjectProp = undefined,
    showCategoryType = false,
    categoryWiseCategoryType = {},
    onCategoryTypeChange = () => {},
    showRankCompetitiveSet = false,
    categoryWiseRankCompetitiveSet = {},
    onRankCompetitiveSetChange = () => {},
    competitorsData = {},
    mainBrand = "",
    keyPlayers = {},
    keyPlayersLoading = false,
}) {
    const dispatch = useDispatch();
    const discoveryCtx = useContext(DiscoveryContext);
    const { user } = useContext(AuthContext);
    const isFinalTransactionCompleted =
        discoveryCtx?.state.finalTransactionStatus === "COMPLETED" ||
        discoveryCtx?.state.finalTransactionStatus === "RETRY";
    const isDisabled = isDisabledProp !== undefined ? isDisabledProp : (discoveryCtx?.loading || isFinalTransactionCompleted);

    // In view mode, enrich options from user_metric_setting_object so IDs resolve to names
    const metricObj = metricSettingObjectProp !== undefined
        ? metricSettingObjectProp
        : (discoveryCtx?.state?.metricSettingObject ?? {});
    const knownValues = new Set(templateOptions.map((o) => String(o.value)));
    const extraOptions = isDisabled
        ? Object.values(metricObj)
            .filter((entry) => entry?.id != null && !knownValues.has(String(entry.id)))
            .map((entry) => ({
                name: entry.name,
                value: entry.id === "default" ? "default" : Number(entry.id),
                isDefault: entry.isDefault ?? false,
            }))
        : [];
    const enrichedOptions = [...templateOptions, ...extraOptions];

    function CustomOption(option) {
        let { name, value, isDefault } = option;
        return (
            <span className={isDefault ? classes.isDefault : classes.isNotDefault}>
                {name}{isDefault ? " ★" : ""}
            </span>
        );
    }

    // Custom option renderer for category-level dropdowns
  function CategoryCustomOption(option) {
    let { name, isDefault } = option;

    return (
        <span className={isDefault ? classes.isDefault : classes.isNotDefault}>
            {name}{isDefault ? " ★" : ""}
        </span>
    );
}
    return (
        <div className={classes.templateSection}>
            {/* Global Template Setting */}
            <div className={classes.globalSection}>
                <div className={classes.sectionHeader}>
                    <div>
                        <h4 className={classes.sectionTitle}>
                            Global Template Settings
                            <InfoOutlined className={classes.infoIcon} titleAccess="This template applies to all categories unless explicitly overridden." />
                        </h4>
                        <p className={classes.sectionSubtitle}>Default template for all categories.</p>
                    </div>
                </div>
                <div className={classes.globalSelectContainer}>
                    <div className={classes.selectWrapper}>
                        <MUISelect
                            fullWidth={true}
                            optionValue={metricSettingId}
                            label=""
                            options={enrichedOptions}
                            disabled={isDisabled}
                            CustomOption={CustomOption}
                            onChange={(e) => {
                                MetricSettingChangeHandle(e.target.value);
                            }}
                        />
                    </div>
                    {!!user?.is_template_setting_enabled && !isDisabled && (
                        <MUIIconButton
                            className={classes.addButton}
                            title="Manage Templates"
                            onClick={() => {
                                dispatch({ type: "ACTION_MODEL", payload: true });
                            }}
                        >
                            <AddCircleOutline />
                        </MUIIconButton>
                    )}
                </div>
            </div>

            {/* Category-Specific Overrides */}
            <div className={classes.overridesSection}>
                <div className={classes.overridesHeader}>
                    <h5 className={classes.overridesTitle}>
                        {showCategoryType ? "Category-Specific Settings" : "Category-Specific Template Settings"}
                    </h5>
                    {/* <span className={classes.overrideBadge}>
                        {overrideCount} Override{overrideCount !== 1 ? 's' : ''} Active
                    </span> */}
                </div>

                <div className={classes.tableContainer}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th>CATEGORY NAME</th>
                                <th>TEMPLATE</th>
                                {showCategoryType && <th>CATEGORY TYPE</th>}
                                {showRankCompetitiveSet && <th className={classes.rankCompetitiveSetCell}>COMPETITIVE SET</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {category.map((cat) => {
                                const isOverride = categoryWiseMetric[cat] &&
                                    categoryWiseMetric[cat] !== metricSettingId;

                                const rawVal = categoryWiseMetric[cat] ?? metricSettingId;
                                const currentValue = rawVal === "default" ? "default" : Number(rawVal);

                                const lockedRankNames = Array.from(new Set([
                                    mainBrand,
                                    ...(competitorsData?.[cat] || []).map((c) => c?.name),
                                ].filter(Boolean)));
                                const additionalRankSelected = (categoryWiseRankCompetitiveSet?.[cat] || [])
                                    .filter((name) => !lockedRankNames.includes(name));
                                const maxAdditionalRank = Math.max(0, MAX_RANK_COMPETITIVE_SET - lockedRankNames.length);
                                const rankCompetitiveSetOptions = (() => {
                                    const apiPlayers = (keyPlayers?.[cat] || [])
                                        .map((player) => ({ name: player.name, value: player.name }))
                                        .filter((p) => !lockedRankNames.includes(p.value));
                                    const existingSelected = additionalRankSelected
                                        .filter((name) => !apiPlayers.some((p) => p.value === name))
                                        .map((name) => ({ name, value: name }));
                                    return [...existingSelected, ...apiPlayers];
                                })();
                                const handleRankCompetitiveSetChange = (selected) => {
                                    const cleaned = selected.filter((n) => !lockedRankNames.includes(n));
                                    onRankCompetitiveSetChange({
                                        category: cat,
                                        value: cleaned.length > maxAdditionalRank
                                            ? cleaned.slice(0, maxAdditionalRank)
                                            : cleaned,
                                    });
                                };

                                return (
                                    <tr key={cat} className={isOverride ? classes.overrideRow : ''}>
                                        <td className={classes.categoryCell}>{cat}</td>
                                        <td className={classes.templateCell}>
                                            <div className={classes.templateCellContent}>
                                                <div className={classes.selectWrapperSmall}>
                                                    <MUISelect
                                                        fullWidth={true}
                                                        optionValue={currentValue}
                                                        label=""
                                                        options={enrichedOptions}
                                                        disabled={isDisabled}
                                                        CustomOption={(option) => CategoryCustomOption(option, metricSettingId)}
                                                        onChange={(e) => {
                                                            onCategoryTemplateChange({
                                                                category: cat,
                                                                value: e.target.value,
                                                            });
                                                        }}
                                                    />
                                                </div>
                                                {/* {isOverride && (
                                                            <span className={classes.overrideLabel}>Override</span>
                                                        )} */}
                                            </div>
                                        </td>
                                        {showCategoryType && (
                                            <td className={classes.templateCell}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder={ENTITY_LABELS.categoryTypePlaceHolder}
                                                    value={categoryWiseCategoryType[cat] ?? ""}
                                                    disabled={isDisabled}
                                                    onChange={(e) => {
                                                        onCategoryTypeChange({
                                                            category: cat,
                                                            value: e.target.value,
                                                        });
                                                    }}
                                                />
                                            </td>
                                        )}
                                        {showRankCompetitiveSet && (
                                            <td className={`${classes.templateCell} ${classes.rankCompetitiveSetCell}`}>
                                                {lockedRankNames.length > 0 && (
                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginBottom: "0.25rem" }}>
                                                        {lockedRankNames.map((name) => (
                                                            <Chip
                                                                key={name}
                                                                label={name}
                                                                size="small"
                                                                title="Selected on the form — always included"
                                                                sx={{
                                                                    background: "#f1f5f9",
                                                                    border: "1px solid #cbd5e1",
                                                                    fontWeight: 600,
                                                                    fontSize: "0.75rem",
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                                <MUIMultiSelect
                                                    loading={keyPlayersLoading}
                                                    disabled={isDisabled}
                                                    fullWidth={true}
                                                    optionValue={""}
                                                    label=""
                                                    placeholder="Add competitors"
                                                    value={additionalRankSelected}
                                                    options={rankCompetitiveSetOptions}
                                                    allowSettingPrimary={false}
                                                    shouldShowValue={true}
                                                    allowCustomOption={!isDisabled}
                                                    onChange={handleRankCompetitiveSetChange}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}